import Box from "@mui/material/Box";
import Alert from "@mui/material/Alert";
import Chip from "@mui/material/Chip";
import Divider from "@mui/material/Divider";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import { useState } from "react";
import type {
  CourtResponse,
  FacilityOperatingHoursDateProjection,
} from "@bw/shared/types/generated";

const SERVICE_LABELS: Record<string, string> = {
  coaching: "Coaching",
  memberships: "Memberships",
};
const CHIP_EXCLUDED = new Set([
  "court_reservation",
  "classes",
  "open_play",
  "coaching",
  "memberships",
]);
const DAY_LABELS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

function serviceLabel(service: string): string {
  const known = SERVICE_LABELS[service];
  if (known) return known;
  const spaced = service.replace(/[_-]+/g, " ").trim();
  return spaced ? spaced.charAt(0).toUpperCase() + spaced.slice(1) : service;
}

function dayLabel(localDate: string): string {
  const dayOfWeek = new Date(`${localDate}T12:00:00Z`).getUTCDay();
  return DAY_LABELS[dayOfWeek] ?? localDate;
}

function timeLabel(raw: string, timezone: string): string {
  return new Intl.DateTimeFormat("en-US", {
    timeZone: timezone,
    hour: "numeric",
    minute: "2-digit",
  }).format(new Date(raw));
}

function intervalLabel(
  opensAt: string,
  closesAt: string,
  timezone: string,
): string {
  const duration = Date.parse(closesAt) - Date.parse(opensAt);
  if (duration === 24 * 60 * 60 * 1000) return "Open 24 hours";
  return `${timeLabel(opensAt, timezone)} – ${timeLabel(closesAt, timezone)}`;
}

/** Server-backed facility facts, amenity actions, and opening hours. */
export function FacilityFacts({
  courts,
  projections,
  enabledServices,
  courtsLoading = false,
  courtsError = false,
  hoursLoading = false,
  hoursError = false,
  publicCourtCount,
}: {
  courts: CourtResponse[];
  projections: FacilityOperatingHoursDateProjection[];
  enabledServices: string[];
  courtsLoading?: boolean;
  courtsError?: boolean;
  hoursLoading?: boolean;
  hoursError?: boolean;
  publicCourtCount?: number;
}) {
  const [showAllHours, setShowAllHours] = useState(false);
  const courtCount = publicCourtCount ?? courts.length;
  const chipServices = enabledServices.filter(
    (service) => !CHIP_EXCLUDED.has(service),
  );

  return (
    <Stack component="section" aria-label="Facility facts" spacing={2.5}>
      {courtCount > 0 ||
      chipServices.length > 0 ||
      courtsLoading ||
      courtsError ? (
        <Stack
          sx={{
            flexDirection: "row",
            alignItems: "center",
            flexWrap: "wrap",
            gap: 1.25,
          }}
        >
          {courtsLoading ? (
            <Typography role="status" color="text.secondary">
              Loading courts…
            </Typography>
          ) : courtsError ? (
            <Alert severity="warning">Couldn&apos;t load court details.</Alert>
          ) : courtCount > 0 ? (
            <Box>
              <Typography component="span" sx={{ fontWeight: 700 }}>
                {courtCount}
              </Typography>{" "}
              <Typography component="span" color="text.secondary">
                {courtCount === 1 ? "court" : "courts"} on-site
              </Typography>
            </Box>
          ) : null}

          {chipServices.map((service) => (
            <Chip
              key={service}
              label={serviceLabel(service)}
              variant="outlined"
            />
          ))}
        </Stack>
      ) : null}
      {courtCount > 0 ||
      chipServices.length > 0 ||
      courtsLoading ||
      courtsError ? (
        <Divider />
      ) : null}

      <Stack spacing={1.25}>
        <Typography component="h2" sx={{ fontSize: "1rem", fontWeight: 700 }}>
          Hours
        </Typography>
        {hoursLoading ? (
          <Typography role="status" color="text.secondary">
            Loading hours…
          </Typography>
        ) : hoursError ? (
          <Alert severity="warning">Couldn&apos;t load operating hours.</Alert>
        ) : projections.length === 0 ? (
          <Typography color="text.secondary">Hours not listed</Typography>
        ) : (
          <Stack
            component="ul"
            spacing={0.75}
            sx={{ p: 0, m: 0, listStyle: "none" }}
          >
            {(showAllHours ? projections : projections.slice(0, 1)).map(
              (projection, index) => (
                <Stack
                  component="li"
                  key={projection.local_date}
                  sx={{
                    display: "grid",
                    gridTemplateColumns: "3em minmax(0, 1fr)",
                    gap: 1.5,
                    py: 0.5,
                  }}
                >
                  <Typography sx={{ fontWeight: 700 }}>
                    {!showAllHours && index === 0
                      ? "Today"
                      : dayLabel(projection.local_date)}
                  </Typography>
                  <Box sx={{ textAlign: "right", overflowWrap: "anywhere" }}>
                    {projection.intervals.length === 0 ? (
                      <Typography color="text.secondary">Closed</Typography>
                    ) : (
                      projection.intervals.map((interval) => (
                        <Typography
                          key={interval.opens_at}
                          color="text.secondary"
                        >
                          {intervalLabel(
                            interval.opens_at,
                            interval.closes_at,
                            projection.timezone,
                          )}
                        </Typography>
                      ))
                    )}
                  </Box>
                </Stack>
              ),
            )}
          </Stack>
        )}
        {projections.length > 1 ? (
          <Button
            size="small"
            sx={{ alignSelf: "flex-start" }}
            onClick={() => setShowAllHours((value) => !value)}
          >
            {showAllHours ? "Show today only" : "See all hours"}
          </Button>
        ) : null}
      </Stack>
    </Stack>
  );
}
